<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <warehouse-offer-invoice-dialog
      v-model="waybillDialog"
      :edited-item="editedItem"
      @success="load"
    />
      <!-- Regular Document View -->
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_invoice") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
          </v-toolbar>
        </template>
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" v-if="false"/>
          </v-row>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.supplier.name="{ item }">
          {{ item.supplier?.name || "-" }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-file-document-outline"
        :title="$t('warehouse.offers_invoices')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_invoice') + ': ' + lastEntryDate
            : $t('no_invoices_yet')
        "
        @click:subtitle="setFiltersToLastEntry()"
      />
  </div>
</template>

<script>
import WarehouseOfferInvoiceDialog from "@/components/warehouse/dialogs/WarehouseOfferInvoiceDialog.vue";
import { WarehouseButtonWaybill } from "@/components/warehouse/buttons";
import { waybillDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import warehouseService from "@/services/warehouse";
import { isInDateRange } from "@/utils/warehouseUtils";

export default {
  name: "WarehouseTabInvoice",
  mixins: [warehouseDocumentTabMixin],
  components: {
    WarehouseOfferInvoiceDialog,
    WarehouseButtonWaybill,
    EmptyState,
    MobileDataCards,
  },
  data: () => ({
    documentType: "offer",
    waybillDialog: false,
    editedItem: { ...waybillDocumentGrocery, groceries: [] },
  }),
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          warehouseService.getOffers({ restaurant_id: this.restaurant?.id }),
          this.$store.dispatch("warehouse/fetchSuppliers"),
        ]);
        this.documents = res.data;
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_loading_documents"));
      } finally {
        this.loading = false;
      }
    },
    openWaybillDialog() {
      this.editedItem = {
        ...waybillDocumentGrocery,
        groceries: [],
        warehouse: this.filters.warehouse[0],
      };
      this.waybillDialog = true;
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.waybillDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_issue"),
          this.$t("warehouse.confirm_delete_issue"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("invoice_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_invoice"));
          console.warn(err);
        }
      }
    },
  },
  computed: {
    filteredDocuments() {
      const { suppliers, documentType, dates, search } = this.filters;
      return this.customSearch(
        this.translatedDocuments,
        this.headers,
        search
      ).filter((item) => {
        const dateMatch = isInDateRange(item, "document_date", dates.from, dates.to);
        const supplierMatch =
          !suppliers.length ||
          suppliers.some((s) => s.id === (item.supplier?.id || item.supplier_id));
        const typeMatch = !documentType || item.document_type === documentType;
        return dateMatch && supplierMatch && typeMatch;
      });
    },
    restaurant() {
      return this.$store.state.restaurant || this.$store.state.company?.restaurant;
    },
    headers() {
      const multiWarehouse = this.filters.warehouse.length > 1;

      return [
        { text: "ID", value: "id", mobile: false },
        {
          text: this.$t("stock"),
          value: "warehouse.name",
          mobile: false,
          show: multiWarehouse,
        },
        { text: this.$t("warehouse.document_number_short"), value: "number", cols: 6 },
        { text: this.$t("date"), value: "document_date", cols: 6 },
        { text: this.$t("supplier"), value: "supplier.name", cols: 6 },
        { text: this.$t("status"), value: "status", mobile: false },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ].filter((h) => h.show !== false);
    },
    exportTitle() {
      const parts = [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("issue"),
      ];
      if (this.aggregateMode) parts.push(this.$t("warehouse.aggregate"));
      return parts.join(" ");
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
}
</style>
